import axios from "axios";
import { isSupplierPublicPath } from "./auth/publicRoutes";
import { readScope, reportReadResult, beginRead } from "./lib/readFailures";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // Fail loudly at boot rather than letting every request hit "undefined/api"
  // and surface as confusing 404s.
  throw new Error("REACT_APP_BACKEND_URL is not set — check frontend/.env");
}
const API_BASE = `${BACKEND_URL}/api`;

// Send the auth cookie on every cross-origin request. Without this the
// session_token cookie never leaves the browser and every API call gets a
// 401 even though the user is signed in.
axios.defaults.withCredentials = true;
axios.interceptors.request.use(config => {
  config._readScope = config._readScope || readScope();
  config._readStartedAt = config._readStartedAt || performance.now();
  beginRead(config);
  return config;
});

// Global 401 handler: bounce to /login if any API call comes back
// unauthenticated. Skips the redirect for the login-flow endpoints
// themselves (which legitimately 401 when there's no session yet).
axios.interceptors.response.use(
  (r) => { reportReadResult(r.config); return r; },
  (err) => {
    reportReadResult(err.config, err);
    if (err?.response?.status === 401) {
      const reqUrl = err.config?.url || "";
      const isAuthFlow = reqUrl.includes("/api/auth/me")
        || reqUrl.includes("/api/auth/session")
        || reqUrl.includes("/api/auth/logout");
      if (!isAuthFlow && !isSupplierPublicPath(window.location.pathname) && window.location.pathname !== "/login" && window.location.pathname !== "/auth/callback") {
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  },
);

// Client errors (4xx) are deterministic — retrying replays the same failure
// and just burns time and server hits. Only network errors, timeouts, and
// 5xx responses are worth retrying.
const isRetryable = (err) => {
  const status = err?.response?.status;
  return status === undefined || status >= 500;
};

/**
 * Resilient API GET with timeout and retries.
 * - Default: 45s timeout, up to 2 retries with exponential backoff
 *   (retry attempts get +5s on the timeout)
 * - 401 throws immediately (global interceptor redirects to /login)
 * - Other 4xx fail immediately without retry
 * - Returns null on final failure (caller handles gracefully)
 */
export async function apiGet(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const { timeout = 45000, retries = 2, params, signal, optional = false } = options;
  const scope = readScope();
  const started = performance.now();

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await axios.get(url, {
        timeout: attempt === 0 ? timeout : timeout + 5000,
        params,
        signal,
        _readScope: scope,
        _readStartedAt: started,
        _readOptional: optional,
      });
      return res.data;
    } catch (err) {
      // Auth failures should not be retried — they're terminal until the
      // user signs in again (handled by the interceptor above).
      if (err?.response?.status === 401 || err.code === "ERR_CANCELED") throw err;
      const isLast = attempt === retries || !isRetryable(err);
      if (isLast) {
        console.error(`API GET ${path} failed after ${attempt + 1} attempt(s):`, err.message);
        return null;
      }
      const backoff = 1000 * Math.pow(2, attempt);
      await new Promise((r) => setTimeout(r, backoff));
    }
  }
  return null;
}

/**
 * API POST with timeout. Does NOT retry by default: POSTs are generally not
 * idempotent, and a retry after a timeout can duplicate the action server-side
 * (double submissions, duplicate emails). Pass { retries: n } explicitly only
 * for endpoints known to be safe to replay.
 * - Throws on final failure (unlike apiGet, which returns null)
 */
export async function apiPost(path, body = {}, options = {}) {
  const url = `${API_BASE}${path}`;
  const { timeout = 45000, retries = 0, params } = options;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await axios.post(url, body, {
        timeout: attempt === 0 ? timeout : timeout + 10000,
        params,
      });
      return res.data;
    } catch (err) {
      if (err?.response?.status === 401) throw err;
      const isLast = attempt === retries || !isRetryable(err);
      if (isLast) {
        console.error(`API POST ${path} failed after ${attempt + 1} attempt(s):`, err.message);
        throw err;
      }
      const backoff = 1000 * Math.pow(2, attempt);
      await new Promise((r) => setTimeout(r, backoff));
    }
  }
}

/**
 * API PUT with timeout. Like apiPost, does not retry by default. Throws on
 * final failure. Used for idempotent config saves (e.g. reminder settings).
 */
export async function apiPut(path, body = {}, options = {}) {
  const url = `${API_BASE}${path}`;
  const { timeout = 45000, params } = options;
  try {
    const res = await axios.put(url, body, { timeout, params });
    return res.data;
  } catch (err) {
    if (err?.response?.status === 401) throw err;
    console.error(`API PUT ${path} failed:`, err.message);
    throw err;
  }
}

/**
 * API DELETE with timeout. Throws on failure.
 */
export async function apiDelete(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const { timeout = 45000, params } = options;
  try {
    const res = await axios.delete(url, { timeout, params });
    return res.data;
  } catch (err) {
    if (err?.response?.status === 401) throw err;
    console.error(`API DELETE ${path} failed:`, err.message);
    throw err;
  }
}

export { API_BASE as API };
